import { useEffect, useRef, useState } from "react";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useWorkflowLock, useUnlockWorkflow } from "../api/WorkflowApi";
import { useActorInfoQuery } from "../api/UsersApi";
import { useFamaFlags } from "../api/FamaFlags";
import { ConfirmationDialog } from "../shared-components/ConfirmationDialog";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import XIcon from "../shared-components/icons/XIcon";
import { getActorDisplayName } from "./reportAssignment";
import { threeSixtyAssigneeOverlay } from "../services/websocket/threeSixtyAssigneeOverlay";

const ASSIGNED_NAME_MAX_LENGTH = 20;

type ThreeSixtyAssignedToCellProps = {
  workflowRunUuid: string;
  lockedBy: string | null | undefined;
  onRefresh?: () => void;
};

function truncateWithEllipsis(name: string, maxLen: number): string {
  if (!name || name.length <= maxLen) return name || "";
  return name.slice(0, maxLen - 1).trim() + "…";
}

function useAssignedTo(
  workflowRunUuid: string,
  lockedBy: string | null | undefined
) {
  const [assignedTo, setAssignedTo] = useState(() =>
    threeSixtyAssigneeOverlay.get(workflowRunUuid, lockedBy)
  );
  const prevLockedByRef = useRef(lockedBy);

  useEffect(() => {
    const normalized = (lockedBy ?? "").trim();
    const prevNormalized = (prevLockedByRef.current ?? "").trim();
    const lockedByChanged = normalized !== prevNormalized;
    prevLockedByRef.current = lockedBy;

    if (lockedByChanged) {
      threeSixtyAssigneeOverlay.set(workflowRunUuid, normalized);
      setAssignedTo(normalized);
      return;
    }

    if (normalized) {
      threeSixtyAssigneeOverlay.set(workflowRunUuid, normalized);
      setAssignedTo(normalized);
      return;
    }

    setAssignedTo(threeSixtyAssigneeOverlay.get(workflowRunUuid, lockedBy));
  }, [lockedBy, workflowRunUuid]);

  useEffect(
    () =>
      threeSixtyAssigneeOverlay.subscribe((uuid, name) => {
        if (uuid === workflowRunUuid) setAssignedTo(name);
      }),
    [workflowRunUuid]
  );

  return assignedTo;
}

export function ThreeSixtyAssignedToCell({
  workflowRunUuid,
  lockedBy,
  onRefresh,
}: ThreeSixtyAssignedToCellProps) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const assignedTo = useAssignedTo(workflowRunUuid, lockedBy);
  const perms = usePermissionValidator();
  const { v3UiReactsToWebsocketWorkflowEvents: liveUpdates } = useFamaFlags();
  const { data: actorInfo } = useActorInfoQuery(true);
  const { mutateAsync: lockWorkflow, isLoading: isLocking } = useWorkflowLock();
  const { mutateAsync: unlockWorkflow, isLoading: isUnlocking } =
    useUnlockWorkflow();

  const isAssigned = Boolean(assignedTo);
  const canClear = perms.canUnlock && isAssigned;

  const handleClaim = async () => {
    try {
      await lockWorkflow({
        workflowRunUUID: workflowRunUuid,
        origin: "360_claim",
      });
      if (liveUpdates) {
        threeSixtyAssigneeOverlay.set(
          workflowRunUuid,
          getActorDisplayName(actorInfo)
        );
      } else {
        onRefresh?.();
      }
    } catch {
      // Error handled by mutation / toast
    }
  };

  const handleClearConfirm = async (action: "close" | "continue") => {
    setShowClearConfirm(false);
    if (action !== "continue") return;
    try {
      await unlockWorkflow({
        workflowRunUUID: workflowRunUuid,
        success: true,
        // No reason: backend logs "Cleared by {full user name}"
      });
      if (liveUpdates) {
        threeSixtyAssigneeOverlay.set(workflowRunUuid, "");
      } else {
        onRefresh?.();
      }
    } catch {
      // Error handled by mutation / toast
    }
  };

  return (
    <div className="flex-1 flex items-center gap-4 min-w-0">
      {!isAssigned ? (
        <Button
          text="Claim"
          type={ButtonTypes.default}
          colorType={ButtonColorTypes.warning}
          onClick={handleClaim}
          disabled={isLocking}
          className="text-xs"
        />
      ) : (
        <>
          <span
            className="truncate max-w-full min-w-0"
            title={assignedTo || undefined}
          >
            {truncateWithEllipsis(assignedTo, ASSIGNED_NAME_MAX_LENGTH)}
          </span>
          {canClear && (
            <button
              type="button"
              onClick={() => setShowClearConfirm(true)}
              disabled={isUnlocking}
              className="flex-shrink-0 w-4 h-4 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center text-gray-400 hover:border-gray-300 hover:text-gray-500 focus:outline-none"
              aria-label="Clear assignment"
            >
              <span className="inline-flex items-center justify-center w-2 h-2 shrink-0 [&>svg]:w-full [&>svg]:h-full">
                <XIcon />
              </span>
            </button>
          )}
        </>
      )}
      <ConfirmationDialog
        isOpen={showClearConfirm}
        title="Remove Assignment?"
        text="Are you sure you want to remove this person from this report? Unassigned work will be available to be claimed by another investigator."
        actionHandler={handleClearConfirm}
        showActionButtons
        confirmButtonText="Yes, Unassign"
        confirmButtonColorType={ButtonColorTypes.warning}
      />
    </div>
  );
}
